import { useEffect, useState } from "react";
import api, { apiError, fmtUSD, fmtNum } from "../../../lib/api";
import { toast } from "sonner";

export function Overview() {
  const [o, setO] = useState(null);
  const [activity, setActivity] = useState([]);
  const [digesting, setDigesting] = useState(false);
  const [digestHour, setDigestHour] = useState(8);
  const [recap, setRecap] = useState({ dow: 0, hour: 8 });
  const [recapping, setRecapping] = useState(false);
  const DOW = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  const ACT = { "admin.grant": "Granted admin", "admin.grant_super": "Granted super admin", "admin.create": "Created admin", "admin.revoke": "Revoked admin", "member.role": "Changed role" };
  useEffect(() => {
    api.get("/admin/overview").then((r) => setO(r.data)).catch(() => {});
    api.get("/admin/admin-activity").then((r) => setActivity(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    api.get("/admin/interest-digest-config").then((r) => setDigestHour(r.data.hour)).catch(() => {});
    api.get("/admin/recap-config").then((r) => setRecap(r.data)).catch(() => {});
  }, []);
  const sendDigest = async () => {
    setDigesting(true);
    try {
      const { data } = await api.post("/admin/interest-digest/send");
      toast.success(data.recipients ? `Interest digest emailed to ${data.recipients} admin${data.recipients === 1 ? "" : "s"}.` : "No announced deals to summarise yet.");
    } catch (e) { toast.error(apiError(e)); } finally { setDigesting(false); }
  };
  const saveHour = async (h) => {
    setDigestHour(h);
    try { await api.put("/admin/interest-digest-config", { hour: h }); toast.success(`Daily digest set for ${String(h).padStart(2, "0")}:00 UTC.`); }
    catch (e) { toast.error(apiError(e)); }
  };
  const saveRecap = async (next) => {
    setRecap(next);
    try { await api.put("/admin/recap-config", next); toast.success(`Weekly recap set for ${DOW[next.dow]} ${String(next.hour).padStart(2, "0")}:00 UTC.`); }
    catch (e) { toast.error(apiError(e)); }
  };
  const sendRecap = async () => {
    setRecapping(true);
    try { const { data } = await api.post("/admin/interest-recap/send"); toast.success(data.sent ? `Interest recap emailed to ${data.sent} member${data.sent === 1 ? "" : "s"}.` : "No members with open interests yet."); }
    catch (e) { toast.error(apiError(e)); } finally { setRecapping(false); }
  };
  const previewRecap = async () => {
    try { const { data } = await api.post("/admin/interest-recap/preview"); toast.success(data.sample ? "Sample recap sent to your email." : "A copy of your recap was sent to your email."); }
    catch (e) { toast.error(apiError(e)); }
  };
  if (!o) return <span className="t-label">Loading</span>;
  return (
    <div data-testid="admin-overview">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
        {[
          ["Members", fmtNum(o.members)], ["Open deals", fmtNum(o.open_deals)],
          ["Committed", fmtUSD(o.committed)], ["Allocated", fmtUSD(o.allocated)],
          ["Settled", fmtUSD(o.settled)], ["Outstanding", fmtUSD(o.outstanding)],
          ["Holdings at mark", fmtUSD(o.holdings_value)], ["Fees accrued", fmtUSD(o.fees_accrued)],
        ].map(([k, v]) => (
          <div key={k} className="hairline pt-4">
            <span className="t-label">{k}</span>
            <div className="fig text-bone text-2xl mt-2" data-testid={`overview-${k.toLowerCase().replace(/ /g, "-")}`}>{v}</div>
          </div>
        ))}
      </div>
      {o.interest && o.interest.total > 0 && (
        <div className="mt-10" data-testid="overview-interest">
          <div className="flex items-center gap-4 flex-wrap">
            <span className="t-label">Deal interest</span>
            <button className="t-label hover:!text-[#E8E6E1]" disabled={digesting} onClick={sendDigest} data-testid="send-digest-button">
              {digesting ? "Sending" : "Email me the digest"}
            </button>
            <span className="t-label">Daily at</span>
            <select className="input-solera fig max-w-[130px] !py-1" value={digestHour} onChange={(e) => saveHour(Number(e.target.value))} data-testid="digest-hour-select">
              {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{`${String(h).padStart(2, "0")}:00 UTC`}</option>)}
            </select>
            <span className="t-label pl-2" style={{ borderLeft: "1px solid var(--rule)" }}>Member recap</span>
            <button className="t-label hover:!text-[#E8E6E1]" disabled={recapping} onClick={sendRecap} data-testid="send-recap-button">
              {recapping ? "Sending" : "Send recap now"}
            </button>
            <button className="t-label hover:!text-[#E8E6E1]" onClick={previewRecap} data-testid="recap-preview-button">Send me a test</button>
            <select className="input-solera max-w-[130px] !py-1" value={recap.dow} onChange={(e) => saveRecap({ ...recap, dow: Number(e.target.value) })} data-testid="recap-dow-select">
              {DOW.map((d, i) => <option key={d} value={i}>{d}</option>)}
            </select>
            <select className="input-solera fig max-w-[130px] !py-1" value={recap.hour} onChange={(e) => saveRecap({ ...recap, hour: Number(e.target.value) })} data-testid="recap-hour-select">
              {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{`${String(h).padStart(2, "0")}:00 UTC`}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-4">
            {[
              ["Committed", fmtNum(o.interest.commit)],
              ["Interested", fmtNum(o.interest.interested)],
              ["Passed", fmtNum(o.interest.pass)],
              ["Conversion", `${o.interest.conversion}%`],
            ].map(([k, v]) => (
              <div key={k} className="hairline pt-4">
                <span className="t-label">{k}</span>
                <div className="fig text-bone text-2xl mt-2" data-testid={`interest-${k.toLowerCase()}`}>{v}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      {activity.length > 0 && (
        <div className="mt-12" data-testid="overview-admin-activity">
          <span className="t-label">Recent admin activity</span>
          <div className="space-y-0 mt-3 max-w-3xl">
            {activity.map((r) => (
              <div key={r.audit_id} className="hairline py-3 flex gap-4 items-baseline flex-wrap" data-testid={`admin-activity-${r.audit_id}`}>
                <span className="t-cap text-muted-s fig">{r.created_at?.slice(0, 16).replace("T", " ")} UTC</span>
                <span className="t-label text-tint">{ACT[r.action] || r.action}</span>
                <span className="t-small text-bone measure">{r.detail}</span>
                <span className="t-cap text-muted-s ml-auto">by {r.actor}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <table className="table-solera mt-12" data-testid="overview-per-deal">
        <thead><tr><th>Deal</th><th>Status</th><th className="num">Committed</th><th className="num">Allocated</th><th className="num">Settled</th><th className="num">Interest</th></tr></thead>
        <tbody>
          {o.per_deal.map((d) => (
            <tr key={d.deal_id}>
              <td className="text-bone">{d.company}</td>
              <td className="text-muted-s">{d.status}</td>
              <td className="num text-bone">{fmtUSD(d.committed)}</td>
              <td className="num text-bone">{fmtUSD(d.allocated)}</td>
              <td className="num text-bone">{fmtUSD(d.settled)}</td>
              <td className="num text-muted-s">
                {d.interest ? `${d.interest.interested + (d.interest.commit || 0)} in` : "-"}
                {d.interest && d.interest.commit ? <span className="block t-cap !text-[#C69A4E]">{d.interest.commit} committed</span> : null}
                {d.interest && d.interest.pending ? <span className="block t-cap text-muted-s">{d.interest.pending} pending</span> : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="t-cap text-muted-s mt-4">Outstanding is allocated less settled. Fees follow each SPV's finders fee basis. Interest counts come from deal announcements.</p>
    </div>
  );
}
